/**
 * Recurring expenses (SPEC.md §6).
 *
 * A rule NEVER inserts an expense on the user's behalf. It is materialised into
 * a pending suggestion and becomes an expense only when they tap confirm.
 *
 * > A silently wrong amount — your phone bill went up — would quietly corrupt
 * > every number in the app. That is the worst failure mode a tracker has, and
 * > it is worth one tap a month to make impossible.
 *
 * Materialisation happens client-side on app open, so it needs no scheduler, no
 * cron, and no server, and it works offline like everything else.
 */

import { clampedStartDay, localYMD } from "./period";
import type { Cents } from "./money";

export interface RecurringLike {
  readonly id: string;
  readonly categoryId: string;
  readonly amountCents: Cents;
  readonly dayOfMonth: number;
  readonly note: string | null;
  readonly active: boolean;
  /** `YYYY-MM-DD` of the last due date already materialised. */
  readonly lastMaterialisedOn: string | null;
}

export interface PendingRecurring {
  readonly rule: RecurringLike;
  /** `YYYY-MM-DD` this instance is due. */
  readonly dueOn: string;
  /** Instant to stamp on the expense if confirmed. */
  readonly occurredAt: Date;
}

function isoDate(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/**
 * The most recent due date at or before `now`, in the user's timezone.
 *
 * Days 29-31 clamp to the last day of a short month, exactly as cycle start days
 * do — a bill due on the 31st is due on 28 February, not skipped that month.
 */
export function currentDueDate(dayOfMonth: number, now: Date, timeZone: string): string {
  const today = localYMD(now, timeZone);
  const dueThisMonth = clampedStartDay(today.year, today.month, dayOfMonth);

  if (today.day >= dueThisMonth) return isoDate(today.year, today.month, dueThisMonth);

  const month = today.month === 1 ? 12 : today.month - 1;
  const year = today.month === 1 ? today.year - 1 : today.year;
  return isoDate(year, month, clampedStartDay(year, month, dayOfMonth));
}

/**
 * The earliest due date still owed, or null if nothing is.
 *
 * `currentDueDate` alone returns only the MOST RECENT due date, which silently
 * drops a skipped month: a rule left unconfirmed on 15 July is replaced by the
 * 15 August instance on 16 August, and July is gone. SPEC §6 says an
 * unconfirmed instance "never silently expires", so this walks forward from the
 * last one actually dealt with and offers the oldest outstanding one first.
 */
export function nextPendingDue(
  dayOfMonth: number,
  lastMaterialisedOn: string | null,
  now: Date,
  timeZone: string,
): string | null {
  const current = currentDueDate(dayOfMonth, now, timeZone);

  // A brand-new rule starts at its most recent due date. Backfilling every
  // month since the beginning of time would be worse than useless.
  if (lastMaterialisedOn === null) return current;
  if (lastMaterialisedOn >= current) return null;

  const [lastYear, lastMonth] = lastMaterialisedOn.split("-").map(Number) as [number, number];
  let year = lastYear;
  let month = lastMonth;

  // Bounded: 24 months is far longer than anyone leaves a bill unconfirmed, and
  // it stops a corrupt `lastMaterialisedOn` spinning here.
  for (let i = 0; i < 24; i++) {
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
    const candidate = isoDate(year, month, clampedStartDay(year, month, dayOfMonth));
    if (candidate > current) return null;
    if (candidate > lastMaterialisedOn) return candidate;
  }
  return current;
}

/**
 * Which rules are due and not yet materialised.
 *
 * `lastMaterialisedOn` is what stops two devices — or one device opened twice in
 * a day — producing the same bill twice. It advances only when the user acts on
 * the suggestion, so an unconfirmed one keeps reappearing rather than expiring
 * silently.
 */
export function pendingRecurring(
  rules: readonly RecurringLike[],
  now: Date,
  timeZone: string,
): PendingRecurring[] {
  const pending: PendingRecurring[] = [];

  for (const rule of rules) {
    if (!rule.active) continue;

    const dueOn = nextPendingDue(rule.dayOfMonth, rule.lastMaterialisedOn, now, timeZone);
    if (dueOn === null) continue;

    pending.push({
      rule,
      dueOn,
      // Noon local, so the expense cannot drift into an adjacent day for a user
      // whose device clock is slightly off.
      //
      // ⚠️ It is the *device's* noon, not the profile's: this form parses as
      // local time, and `timeZone` is in scope here and not used for it. So the
      // drift the anchor exists to prevent still happens once the two disagree
      // by more than 12 hours — a Honolulu device on an Asia/Singapore profile
      // files this under the next day. Visible since the Ledger started showing
      // the hour; the fix is `zonedMidnight(dueOn, timeZone)` plus twelve hours.
      // Do NOT copy this pattern: see ROADMAP Phase 19, "Known, not fixed".
      occurredAt: new Date(`${dueOn}T12:00:00`),
    });
  }

  return pending.sort((a, b) => a.dueOn.localeCompare(b.dueOn));
}
